import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Pencil, FolderOpen } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { uploadWithProgress, formatSize } from "@/lib/uploads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DocumentRow, DocumentFilters, filterDocuments, useDocuments, EMPTY_FILTERS } from "@/pages/Documents";

const EMPTY_FORM = { title: "", category: "regolamenti", description: "", event_id: "", discipline: "" };

function DocumentForm({ form, setForm, events, categories, file, setFile, isEdit }) {
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="space-y-3">
      {!isEdit && (
        <div>
          <Label>File *</Label>
          <label data-testid="document-file-label" className="mt-1 flex items-center gap-2 text-sm px-3 py-2.5 rounded-md border border-dashed border-white/20 hover:border-gold/50 cursor-pointer bg-[#0B1220]">
            <Upload className="w-4 h-4 text-gold shrink-0" />
            <span className="truncate">{file ? `${file.name} (${formatSize(file.size)})` : "Scegli un file (qualsiasi formato)"}</span>
            <input data-testid="document-file-input" type="file" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setFile(f); if (!form.title) upd("title")(f.name.replace(/\.[^.]+$/, "")); } }} />
          </label>
        </div>
      )}
      <div><Label>Titolo</Label><Input data-testid="document-title-input" value={form.title} onChange={(e) => upd("title")(e.target.value)} placeholder="Es. Regolamento tecnico 2026" className="mt-1 bg-[#0B1220] border-white/15" /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>Categoria</Label>
          <Select value={form.category} onValueChange={upd("category")}>
            <SelectTrigger data-testid="document-category-select" className="mt-1 bg-[#0B1220] border-white/15"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(categories).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Disciplina</Label>
          <Select value={form.discipline || "none"} onValueChange={(v) => upd("discipline")(v === "none" ? "" : v)}>
            <SelectTrigger data-testid="document-discipline-select" className="mt-1 bg-[#0B1220] border-white/15"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="none">Tutte / nessuna</SelectItem><SelectItem value="artistica">Artistica</SelectItem><SelectItem value="ritmica">Ritmica</SelectItem></SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Gara collegata (facoltativo)</Label>
        <Select value={form.event_id || "none"} onValueChange={(v) => upd("event_id")(v === "none" ? "" : v)}>
          <SelectTrigger data-testid="document-event-select" className="mt-1 bg-[#0B1220] border-white/15"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nessuna gara</SelectItem>
            {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title} — {e.date}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div><Label>Descrizione</Label><Textarea data-testid="document-description-input" value={form.description} onChange={(e) => upd("description")(e.target.value)} rows={2} className="mt-1 bg-[#0B1220] border-white/15" /></div>
    </div>
  );
}

export default function DocumentsSection() {
  const { documents, categories, loading, reload } = useDocuments();
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/events").then((r) => setEvents(r.data || [])).catch(() => {}); }, []);
  const filtered = useMemo(() => filterDocuments(documents, filters), [documents, filters]);

  const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setFile(null); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ title: d.title, category: d.category, description: d.description || "", event_id: d.event_id || "", discipline: d.discipline || "" }); setOpen(true); };

  const save = async () => {
    if (!editing && !file) { toast.error("Seleziona un file da caricare"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/documents/${editing.id}`, form);
      else await uploadWithProgress("/documents", file, form);
      toast.success(editing ? "Documento aggiornato" : "Documento pubblicato nell'Area Documenti");
      setOpen(false);
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Operazione non riuscita"); }
    finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare "${d.title}"?`)) return;
    try { await api.delete(`/documents/${d.id}`); toast.success("Documento eliminato"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eliminazione non riuscita"); }
  };

  return (
    <div className="space-y-6" data-testid="documents-section">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><FolderOpen className="w-5 h-5 text-gold" /> Area Documenti pubblica</h2>
          <p className="text-sm text-muted-foreground mt-1">Regolamenti, moduli, circolari e verbali visibili a tutti su <a href="/documenti" target="_blank" rel="noopener noreferrer" className="text-gold underline">/documenti</a>. I verbali ufficiali delle gare chiuse vengono inclusi automaticamente.</p>
        </div>
        <Button data-testid="add-document-btn" onClick={openNew} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold"><Upload className="w-4 h-4 mr-2" /> Carica documento</Button>
      </div>

      <DocumentFilters docs={documents} categories={categories} filters={filters} setFilters={setFilters} />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
        : filtered.length === 0 ? <div data-testid="documents-admin-empty" className="text-center py-16 text-muted-foreground border border-dashed border-white/10 rounded-xl">Nessun documento. Carica il primo con "Carica documento".</div>
        : (
          <div className="space-y-3" data-testid="documents-admin-list">
            {filtered.map((d) => (
              <DocumentRow key={d.id} doc={d} actions={d.source === "upload" && (
                <>
                  <button data-testid={`document-edit-${d.id}`} onClick={() => openEdit(d)} title="Modifica" className="p-2 rounded-full border border-white/15 hover:border-gold/60 hover:text-gold"><Pencil className="w-3.5 h-3.5" /></button>
                  <button data-testid={`document-delete-${d.id}`} onClick={() => remove(d)} title="Elimina" className="p-2 rounded-full border border-white/15 hover:border-destructive hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                </>
              )} />
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Modifica documento" : "Carica documento"}</DialogTitle><DialogDescription>{editing ? "Aggiorna titolo, categoria o gara collegata." : "Il documento sarà visibile pubblicamente nell'Area Documenti."}</DialogDescription></DialogHeader>
          <DocumentForm form={form} setForm={setForm} events={events} categories={categories} file={file} setFile={setFile} isEdit={!!editing} />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-white/15">Annulla</Button>
            <Button data-testid="document-save-btn" onClick={save} disabled={saving} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Salva" : "Pubblica"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
